import React, { useMemo } from "react";
import {
	ArrowUpRight,
	ChevronDown,
	ChevronUp,
	PenLine,
	Sparkles,
} from "lucide-react";
import PriorityPill from "./PriorityPill";
import CoverageBar from "./CoverageBar";
import { ProviderIcon } from "../../shared/ProviderIcon";
import { PROVIDER_ORDER } from "./providerMap";
import { hasSignalCoverage, signalName } from "./strategyShape";
import { TIER_DOT_CLASS, tierOf } from "./tierColors";

function GapByProvider({ signalIds, crossProviderSignals }) {
	const signals = signalIds
		.map((id) => crossProviderSignals.find((s) => s.id === id))
		.filter(Boolean);

	if (signals.length === 0) return null;

	return (
		<div className="monitoring__analysis-strategy-gap-by-provider">
			<span className="small text-muted">Gap by provider</span>
			<div className="monitoring__analysis-strategy-gap-provider-rows">
				{PROVIDER_ORDER.map((p) => {
					const count = signals.filter((s) => hasSignalCoverage(s, p)).length;
					return (
						<div key={p} className="monitoring__analysis-strategy-gap-provider-row">
							<ProviderIcon provider={p} size={12} />
							<span className="small">{count}</span>
						</div>
					);
				})}
			</div>
			<div className="monitoring__analysis-strategy-gap-dots">
				{signals.slice(0, 4).map((s) => (
					<span
						key={s.id}
						className={`monitoring__analysis-strategy-tier-dot ${TIER_DOT_CLASS[tierOf(s.coverage_count)]
							}`}
						title={s.name}
					/>
				))}
				{signals.length > 4 && (
					<span className="small text-muted">+{signals.length - 4}</span>
				)}
			</div>
		</div>
	);
}

export default function OptimizeContentRow({
	rec,
	crossProviderSignals,
	detailOpen,
	onToggleDetail,
	onViewGapAnalysis,
	writingRunId,
	onUseWritingAssistant,
	onViewContent,
}) {
	const gapCount = rec.signals_to_check?.length ?? 0;
	const signalLabels = useMemo(
		() =>
			(rec.signals_to_check ?? []).map((id) =>
				signalName(id, crossProviderSignals)
			),
		[rec.signals_to_check, crossProviderSignals]
	);

	return (
		<div className="monitoring__analysis-strategy-rec-row monitoring__analysis-strategy-rec-row--optimize">
			<div className="monitoring__analysis-strategy-rec-row-priority">
				<PriorityPill priority={rec.priority} />
			</div>
			<div className="monitoring__analysis-strategy-rec-row-main">
				<p className="fw-semibold small mb-1">{rec.title}</p>
				<a
					href={rec.target.url}
					target="_blank"
					rel="noopener noreferrer"
					className="small d-inline-flex align-items-center gap-1"
				>
					{rec.target.url} <ArrowUpRight size={12} aria-hidden />
				</a>
			</div>
			<div className="monitoring__analysis-strategy-rec-row-gaps">
				{/* <span className="small fw-semibold">Overall gaps: {gapCount}</span> */}
				{gapCount > 0 && (
					<GapByProvider
						signalIds={rec.signals_to_check}
						crossProviderSignals={crossProviderSignals}
					/>
				)}
			</div>
			<div className="monitoring__analysis-strategy-rec-row-impact">
				<CoverageBar impact={rec.provider_impact} />
			</div>
			<div className="monitoring__analysis-strategy-rec-row-actions">
				{writingRunId != null ? (
					<button
						type="button"
						className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
						onClick={() => onViewContent?.(writingRunId)}
					>
						<PenLine size={13} aria-hidden /> View Content
					</button>
				) : (
					<button
						type="button"
						className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
						onClick={onUseWritingAssistant}
					>
						<Sparkles size={13} aria-hidden /> Use AI Writing Assistant
					</button>
				)}
				{gapCount > 0 && (
					<button
						type="button"
						onClick={onToggleDetail}
						className="monitoring__analysis-strategy-show-more btn btn-link btn-sm p-0 d-block"
					>
						Signals {detailOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
					</button>
				)}
				{/* {onViewGapAnalysis && (
					<button
						type="button"
						onClick={onViewGapAnalysis}
						className="monitoring__analysis-strategy-show-more btn btn-link btn-sm p-0 d-block"
					>
						View Gap Analysis
					</button>
				)} */}
			</div>
			{detailOpen && gapCount > 0 && (
				<div className="monitoring__analysis-strategy-rec-row-detail">
					<ul className="monitoring__analysis-strategy-signal-ul-list">
						{signalLabels.map((label, i) => (
							<li
								key={rec.signals_to_check[i]}
								className="monitoring__analysis-strategy-signal-ul-list-item"
							>
								<span aria-hidden>•</span>
								{label}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}
